import * as Sentry from '@sentry/browser'
import { createStore, applyMiddleware, combineReducers } from 'redux'
import { connectRoutes } from 'redux-first-router'
import restoreScroll from 'redux-first-router-restore-scroll'
import { composeWithDevTools } from '@redux-devtools/extension'
import createSagaMiddleware from 'redux-saga'
import queryString from 'query-string'

import * as routes from 'ducks/router'
import reducers from './reducer'
import rootSaga from './sagas'

const PRODUCTION = process.env.PRODUCTION

const configureStore = (
  initialState: Record<string, unknown> = {},
  routeHistory: (string | { pathname: string })[] = [],
) => {
  const router = connectRoutes(routes.routesMap, {
    initialDispatch: false,
    initialEntries: routeHistory as { pathname: string }[],
    restoreScroll: restoreScroll(),
    querySerializer: {
      parse: (query: string) =>
        queryString.parse(query, { arrayFormat: 'comma' }),
      stringify: (params: Record<string, unknown>) =>
        queryString.stringify(params, { arrayFormat: 'comma' }),
    },
  })
  const sagaMiddleware = createSagaMiddleware({
    onError: PRODUCTION ? Sentry.captureException : console.error,
  })
  const middlewares = [router.middleware, sagaMiddleware]
  const composeEnhancers = composeWithDevTools({
    actionsDenylist: ['timer/UPDATE_TIMER'],
    trace: true,
  })
  const enhancers = composeEnhancers(
    router.enhancer,
    applyMiddleware(...middlewares),
  )
  const rootReducer = combineReducers({
    [routes.SLICE]: router.reducer,
    ...reducers,
  })
  const store = createStore(rootReducer, initialState, enhancers)
  let sagaTask = sagaMiddleware.run(rootSaga)

  router.initialDispatch?.()

  if (module.hot) {
    module.hot.accept('./reducer', () => {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const reducers = require('./reducer').default
      const newReducer = combineReducers({
        [routes.SLICE]: router.reducer,
        ...reducers,
      })
      store.replaceReducer(newReducer as unknown as typeof rootReducer)
    })
    // Hot module replacement for saga
    module.hot.accept('./sagas/', () => {
      sagaTask.cancel() // stop old saga
      sagaTask.toPromise().then(() => {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        sagaTask = sagaMiddleware.run(require('./sagas/').default)
      })
    })
  }
  return store
}

export default configureStore
